import { useEffect } from "react";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import "../../styles/home.css";
import "../../styles/how-prufyy-works.css";
import { initScrollReveal } from "../../utils/scrollReveal";
import { initScrollProgress } from "../../utils/scrollProgress";

function Founder() {
  useEffect(() => {
    initScrollReveal();
    initScrollProgress();
  }, []);

  return (
    <>
      <Header />

      {/* SCROLL PROGRESS */}
      <div className="scroll-progress">
        <div className="scroll-progress-fill" />
      </div>

      <main className="hpw-wrapper">
        {/* ================= HERO ================= */}
        <section className="hero-section reveal">
          <div className="hero-content">
            <h1>
              Fundraising Is Not the Problem <br />
              Credibility Translation Is
            </h1>
            <p>
              Founders are not lacking conviction or effort. What breaks down is
              how startup claims are interpreted, questioned, and verified across
              different decision-makers.
            </p>
          </div>
        </section>

        {/* ================= AUDIENCE ================= */}
        <section className="section reveal">
          <span className="audience-badge">Founder Perspective</span>
          <h2>For Founders Navigating Repeated Evaluation</h2>
          <p>
            Every pitch compresses months or years of work into a narrative.
            Investors and evaluators then interpret that narrative through their
            own lenses, often asking different questions about the same claims.
          </p>
          <p>
            Prufyy exists to reduce this translation gap — without changing your
            story or replacing human judgment.
          </p>
        </section>

        {/* ================= PROBLEM ================= */}
        <section className="section reveal">
          <h2>Where Founders Lose Credibility Unintentionally</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>Buried Claims</h4>
              <p>Important claims get lost inside storytelling.</p>
            </div>

            <div className="info-card">
              <h4>Diluted Strengths</h4>
              <p>Strong areas are weakened by less credible ones.</p>
            </div>

            <div className="info-card">
              <h4>Scattered Proof</h4>
              <p>Evidence lives across folders, links, and follow-ups.</p>
            </div>

            <div className="info-card">
              <h4>Repeated Diligence</h4>
              <p>The same questions surface in every conversation.</p>
            </div>

            <div className="info-card">
              <h4>Unclear Feedback</h4>
              <p>Responses feel vague or inconsistent.</p>
            </div>
          </div>
        </section>

        {/* ================= NEED ================= */}
        <section className="section reveal">
          <h2>What Founders Actually Need</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>Claim Separation</h4>
              <p>A way to separate claims from persuasion.</p>
            </div>

            <div className="info-card">
              <h4>Focused Proof</h4>
              <p>A way to attach proof only where scrutiny exists.</p>
            </div>

            <div className="info-card">
              <h4>Portable Profile</h4>
              <p>A single profile that travels across evaluations.</p>
            </div>

            <div className="info-card">
              <h4>Credibility Clarity</h4>
              <p>Understanding how claims are perceived.</p>
            </div>

            <div className="info-card">
              <h4>Sharing Control</h4>
              <p>Control over who sees what — and for how long.</p>
            </div>
          </div>
        </section>

        {/* ================= STRUCTURE ================= */}
        <section className="section reveal">
          <h2>How Prufyy Introduces Structure Without Changing Your Pitch</h2>

          <div className="flow-diagram">
            <div className="flow-step">
              <div className="flow-icon">📄</div>
              <h3>Narrative Intake</h3>
              <p>Your pitch remains your pitch.</p>
            </div>

            <div className="flow-step">
              <div className="flow-icon">🧩</div>
              <h3>Claims Isolated</h3>
              <p>Key statements are separated and grouped.</p>
            </div>

            <div className="flow-step">
              <div className="flow-icon">📎</div>
              <h3>Evidence Attached</h3>
              <p>Proof is linked only where credibility matters.</p>
            </div>

            <div className="flow-step">
              <div className="flow-icon">🔗</div>
              <h3>Structured Sharing</h3>
              <p>One profile shared intentionally.</p>
            </div>
          </div>
        </section>

        {/* ================= EXAMPLE ================= */}
        <section className="section reveal">
          <h2>Example: A Founder Claim, Done Right</h2>

          <div className="claim-evidence">
            <div className="claim-box">
              <strong>Claim</strong>
              <p>“We have strong early enterprise traction.”</p>
            </div>

            <div className="link-line" />

            <div className="evidence-box">
              <strong>Evidence</strong>
              <ul>
                <li>Signed letters of intent</li>
                <li>Early revenue statements</li>
                <li>Customer reference calls</li>
              </ul>
            </div>
          </div>

          <p style={{ marginTop: 24 }}>
            Evaluators no longer need to infer what supports the claim. They
            inspect it directly, in context.
          </p>
        </section>

        {/* ================= BEFORE / AFTER ================= */}
        <section className="section reveal">
          <h2>Before vs After Prufyy</h2>

          <div className="before-after-static">
            <div className="before-card">
              <h3>Before</h3>
              <ul>
                <li>Re-explaining context repeatedly</li>
                <li>Documents shared reactively</li>
                <li>Unclear or conflicting feedback</li>
              </ul>
            </div>

            <div className="after-card">
              <h3>After</h3>
              <ul>
                <li>Claims reviewed independently</li>
                <li>Evidence inspected in place</li>
                <li>Focused, high-signal conversations</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ================= FEEDBACK ================= */}
        <section className="section reveal">
          <h2>Credibility Feedback Without Judgment</h2>
          <p>
            Prufyy does not score or rank startups. Founders receive aggregated
            interpretation of how claims are perceived — without exposing
            evaluator identities or turning feedback into a verdict.
          </p>

          <div className="info-cards">
            <div className="info-card">
              <h4>No Scores</h4>
              <p>Numerical grading is intentionally avoided.</p>
            </div>

            <div className="info-card">
              <h4>No Rankings</h4>
              <p>Founders are never ordered or compared publicly.</p>
            </div>

            <div className="info-card">
              <h4>No Exposure</h4>
              <p>Evaluator identities remain private.</p>
            </div>

            <div className="info-card">
              <h4>Actionable Clarity</h4>
              <p>Feedback focuses on what to strengthen next.</p>
            </div>
          </div>
        </section>

        {/* ================= CONTROL ================= */}
        <section className="section reveal">
          <h2>Sharing Without Losing Control</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>Intentional Sharing</h4>
              <p>Profiles are shared deliberately.</p>
            </div>

            <div className="info-card">
              <h4>Revocable Access</h4>
              <p>Access can be removed at any time.</p>
            </div>

            <div className="info-card">
              <h4>Time-Bound Links</h4>
              <p>Public access expires automatically.</p>
            </div>

            <div className="info-card">
              <h4>No Over-Sharing</h4>
              <p>Nothing is exposed by default.</p>
            </div>
          </div>
        </section>

        {/* ================= FINAL CTA ================= */}
        <section className="final-cta-section reveal">
          <h2>Structure Your Startup’s Credibility</h2>
          <p>
            Prufyy helps founders focus conversations on substance — not
            persuasion theatrics.
          </p>
          <a href="/signup" className="btn-primary">
            Create Your Prufyy Profile
          </a>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default Founder;
